import {z} from 'zod';
import {format} from 'date-fns';
import React, {FC} from 'react';
import {toast} from 'react-toastify';
import {observer} from 'mobx-react-lite';
import {useNavigate} from 'react-router-dom';
import {zodResolver} from '@hookform/resolvers/zod';
import {useForm, FormProvider} from 'react-hook-form';
import {ArrowRightIcon} from '@heroicons/react/24/outline';
import {convertToE164, IProfile, updateProfilePhoto} from '@verdocs/js-sdk';
import {getCurrentProfile, updateProfile, VerdocsEndpoint} from '@verdocs/js-sdk';
import {Button, FlexFill, FormHint, HelpDialog, PageTitle, PhotoSelector, SelectInput, TextInput} from '../../Components';
import {invalidateAllQueries, useCurrentProfile} from '../../react-sdk/queries';
import {track} from '../../Managers/Analytics';
import {CONFIG} from '../../config';
import {URLS} from '../../Routing';

const schema = z.object({
  first_name: z.string().trim().min(1, 'First name is required').max(30),
  last_name: z.string().trim().min(1, 'Last name is required').max(30),
  email: z.email().trim(),
  phone: z.union([z.literal(''), z.string().trim()]).pipe(z.transform((val) => convertToE164(val))),
  timezone: z.string().nullish(),
  locale: z.string().nullish(),
});

type SchemaType = z.infer<typeof schema>;

const LOCALE_OPTIONS = [
  {label: 'English (United States)', value: 'en-US'},
  {label: 'English (United Kingdom)', value: 'en-GB'},
  {label: 'English (Canada)', value: 'en-CA'},
  {label: 'English (Australia)', value: 'en-AU'},
  {label: 'Spanish (Spain)', value: 'es-ES'},
  {label: 'Spanish (Mexico)', value: 'es-MX'},
  {label: 'French (France)', value: 'fr-FR'},
  {label: 'French (Canada)', value: 'fr-CA'},
  {label: 'German (Germany)', value: 'de-DE'},
  {label: 'Italian (Italy)', value: 'it-IT'},
  {label: 'Portuguese (Brazil)', value: 'pt-BR'},
  {label: 'Dutch (Netherlands)', value: 'nl-NL'},
  {label: 'Japanese (Japan)', value: 'ja-JP'},
];

const getEditableFields = (profile?: IProfile) => ({
  first_name: profile?.first_name || '',
  last_name: profile?.last_name || '',
  email: profile?.email || '',
  phone: profile?.phone || '',
  timezone: profile?.timezone || '',
  locale: profile?.locale || '',
});

// Every browser we support has Intl.supportedValuesOf; the short list is only a guard for the ones that do not.
const FALLBACK_TIMEZONES = [
  'America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles', 'America/Anchorage', 'Pacific/Honolulu',
  'America/Toronto', 'America/Vancouver', 'America/Mexico_City', 'America/Sao_Paulo', 'Europe/London', 'Europe/Paris',
  'Europe/Berlin', 'Europe/Madrid', 'Asia/Tokyo', 'Asia/Singapore', 'Asia/Kolkata', 'Australia/Sydney', 'UTC',
];
const TIMEZONES: string[] = ((Intl as any).supportedValuesOf?.('timeZone') as string[] | undefined) || FALLBACK_TIMEZONES;
const TIMEZONE_OPTIONS = TIMEZONES.map((tz) => ({value: tz, label: tz.replace(/_/g, ' ')}));

const Card: FC<{children: React.ReactNode; className?: string}> = ({children, className = ''}) => (
  <div className={`rounded-lg border border-[#ebecf0] bg-white p-5 ${className}`}>{children}</div>
);

const SectionTitle: FC<{children: React.ReactNode}> = ({children}) => (
  <div className="mb-3 text-[15px] font-medium text-[#33364b]">{children}</div>
);

const roleLabel = (profile?: IProfile) => {
  const roles = profile?.roles || [];
  return roles.includes('owner') ? 'Owner' : roles.includes('admin') ? 'Admin' : 'Member';
};

export const Profile: FC = observer(() => {
  const navigate = useNavigate();
  const [newProfilePhoto, setNewProfilePhoto] = React.useState<File | null>(null);
  const currentProfile = useCurrentProfile();

  const form = useForm<SchemaType>({
    mode: 'all',
    resolver: zodResolver(schema),
    defaultValues: async () => {
      return getCurrentProfile(VerdocsEndpoint.getDefault()).then(getEditableFields);
    },
  });

  const onSubmit = async (data: any) => {
    if (newProfilePhoto) {
      await updateProfilePhoto(VerdocsEndpoint.getDefault(), currentProfile!.id, newProfilePhoto)
        .then(() => setNewProfilePhoto(null))
        .catch((e) => console.log('Unknown error', e));
    }

    await updateProfile(VerdocsEndpoint.getDefault(), currentProfile!.id, {
      first_name: data.first_name,
      last_name: data.last_name,
      phone: data.phone,
      timezone: data.timezone || null,
      locale: data.locale || null,
    })
      .then((r) => {
        form.reset(getEditableFields(r));
        invalidateAllQueries();
        track('settings_updated', {section: 'profile'});
        toast.success('Profile updated');
      })
      .catch((e) => toast.error(`Error updating profile: ${e.message}`));
  };

  const firstName = form.watch('first_name');
  const lastName = form.watch('last_name');
  const isPersonal = currentProfile?.organization_id === CONFIG.PERSONAL_ORGID;

  return (
    <div className="w-full max-w-[860px] pb-10">
      <PageTitle>
        My Profile
        <FlexFill />
        <HelpDialog title="My profile">
          <p>
            Your profile is how you appear to other members of your organization and to anyone you send documents to. If you share
            templates publicly, your name and photo may be visible outside your organization; your email address and phone number are not.
          </p>
          <p>
            Your email address is your sign-in and cannot be changed here. Contact <a href="mailto:support@verdocs.com">support@verdocs.com</a>{' '}
            to change it.
          </p>
        </HelpDialog>
      </PageTitle>

      <FormProvider {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <Card className="mb-4">
            <div className="flex flex-wrap items-center gap-5">
              <PhotoSelector rounded newFile={newProfilePhoto} value={currentProfile?.picture} onSelectFile={setNewProfilePhoto} />

              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="truncate text-xl font-medium text-[#33364b]">
                    {firstName} {lastName}
                  </span>
                  {!isPersonal && (
                    <span className="rounded-full bg-[#efedfb] px-2 py-0.5 text-[11px] font-medium text-[#654dcb]">{roleLabel(currentProfile)}</span>
                  )}
                </div>
                <div className="mt-0.5 truncate text-[13px] text-gray-500">{currentProfile?.email}</div>
                <div className="mt-0.5 text-[12px] text-gray-400">
                  {isPersonal ? 'Personal account' : currentProfile?.organization?.name}
                  {currentProfile?.created_at ? ` · joined ${format(new Date(currentProfile.created_at), 'MMM yyyy')}` : ''}
                </div>
              </div>
            </div>
          </Card>

          <Card className="mb-4">
            <SectionTitle>Your details</SectionTitle>
            <div className="grid grid-cols-1 gap-x-4 tablet:grid-cols-2">
              <TextInput label="First name" name="first_name" required />
              <TextInput label="Last name" name="last_name" required />
            </div>

            <TextInput
              label="Email"
              name="email"
              disabled
              hint={
                <>
                  Your email address is your sign-in and cannot be changed here. Contact{' '}
                  <a className="text-[#654dcb] no-underline hover:underline" href="mailto:support@verdocs.com">
                    support@verdocs.com
                  </a>{' '}
                  to change it.
                </>
              }
            />

            <div className="grid grid-cols-1 gap-x-4 tablet:grid-cols-2">
              <TextInput label="Phone" name="phone" placeholder="+1 555 555 5555" hint="Used for text message verification when a document requires it." />
              <SelectInput
                label="Time zone"
                name="timezone"
                options={TIMEZONE_OPTIONS}
                placeholder="Browser default"
                hint="Dates and times across Verdocs are shown in this zone."
              />
              <SelectInput label="Language and region" name="locale" options={LOCALE_OPTIONS} placeholder="Browser default" hint="Date and number formats" />
            </div>

            <Button type="submit" label="Save changes" disabled={form.formState.isSubmitting || (!form.formState.isDirty && !newProfilePhoto)} />
          </Card>

          <Card>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <SectionTitle>Signing in and security</SectionTitle>
                <FormHint className="mt-0">Password, two-factor authentication, and active sessions are managed separately.</FormHint>
              </div>
              <Button
                size="small"
                variant="secondary"
                label="Security settings"
                icon={<ArrowRightIcon className="h-4 w-4" />}
                onClick={() => navigate(URLS.SETTINGS_SECURITY.build())}
              />
            </div>
          </Card>
        </form>
      </FormProvider>
    </div>
  );
});
